import { storeConfig } from '@/shared/storeConfig';
import { useAuthStore } from '@/stores/auth';
import { useSmarticoMiniGamesHistory } from '~/stores/smartico/miniGamesHistory';
import { useSmarticoInit } from '~/stores/smartico/init';

export const useSmarticoMiniGames = defineStore(
  'smarticoMiniGames',
  () => {
    const { debugLog } = useSmarticoConfigs();
    const authStore = useAuthStore();

    const miniGames = ref<TMiniGameTemplate[]>([]);
    const isLoading = ref<boolean>(false);
    const isCallbackRegistered = ref<boolean>(false);
    const gamePlayed = ref<boolean>(false); // New flag to indicate if a game was played
    const customUpdateCallbacks = ref<
      Array<(data: TMiniGameTemplate[]) => void>
    >([]);

    let refreshTimeout: NodeJS.Timeout | null = null;

    function setGamePlayed(status: boolean) {
      gamePlayed.value = status;
    }

    function triggerDelayedRefresh() {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      refreshTimeout = setTimeout(async () => {
        debugLog(
          '⏰ Delayed refresh triggered: Reloading mini-games and history',
        );
        await loadMiniGames();

        const smarticoHistoryStore = useSmarticoMiniGamesHistory();

        await smarticoHistoryStore.loadHistory();
        gamePlayed.value = false; // Reset after refresh
      }, 10000); // 10 seconds
    }

    function handleMiniGamesUpdate(updatedMiniGames: TMiniGameTemplate[]) {
      if (!updatedMiniGames || !Array.isArray(updatedMiniGames)) {
        debugLog('⚠️ onUpdate callback received invalid data for mini-games');

        return;
      }

      debugLog(
        `🔄 Mini-games updated via callback onUpdate: ${updatedMiniGames.length} mini-games`,
      );
      miniGames.value = updatedMiniGames;

      if (customUpdateCallbacks.value.length > 0) {
        customUpdateCallbacks.value.forEach((callback) => {
          try {
            callback(updatedMiniGames);
          } catch (err: any) {
            debugLog(
              `⭕️ ~ custom callback execution error ~ ${err.toString()}`,
            );
          }
        });
      }
    }

    function registerUpdateCallback() {
      if (isCallbackRegistered.value) {
        return;
      }

      try {
        if (window && window._smartico) {
          const api = authStore.isAuthenticated
            ? window._smartico.api
            : window._smartico.vapi('EN');

          api.registerMiniGamesUpdateCallback?.(handleMiniGamesUpdate);
          isCallbackRegistered.value = true;
          debugLog('✅ Mini-games update callback registered successfully');
        }
      } catch (err: any) {
        debugLog(`⭕️ ~ registerMiniGamesUpdateCallback ~ ${err.toString()}`);
      }
    }

    function waitForSmartico(timeout = 15000, interval = 500) {
      return new Promise((resolve, reject) => {
        const startTime = Date.now();
        const checkSmartico = () => {
          // Verifica se o Smartico está carregado
          if (window && window._smartico) {
            // Para usuários autenticados, espera a api estar disponível
            if (authStore.isAuthenticated && window._smartico.api) {
              resolve(true);

              return;
            }
            // Para visitantes, espera vapi estar disponível
            if (!authStore.isAuthenticated && window._smartico.vapi) {
              resolve(true);

              return;
            }
          }

          if (Date.now() - startTime > timeout) {
            reject(new Error('Smartico API not available within timeout'));
          } else {
            setTimeout(checkSmartico, interval);
          }
        };

        checkSmartico();
      });
    }

    async function loadMiniGames(): Promise<void> {
      try {
        isLoading.value = true;

        await waitForSmartico(); // Wait for Smartico to be ready

        registerUpdateCallback();

        let miniGamesPromise;

        if (authStore.isAuthenticated) {
          miniGamesPromise = window._smartico.api.getMiniGames({
            onUpdate: handleMiniGamesUpdate,
          });
        } else {
          miniGamesPromise = window._smartico.vapi('EN').getMiniGames({
            onUpdate: handleMiniGamesUpdate,
          });
        }

        const initialMiniGames = await miniGamesPromise;

        miniGames.value = initialMiniGames;

        debugLog(`📊 Loaded ${miniGames.value.length} mini-games initially`);
      } catch (err: any) {
        debugLog(`⭕️ ~ getMiniGames ~ ${err.toString()}`);
        console.error('Error loading mini-games:', err); // Adicionar log de erro
      } finally {
        isLoading.value = false;
      }
    }

    function waitForSmarticoReady(timeoutMs = 5000): Promise<boolean> {
      const { isReadySmartico, isSuccessCheckUserIdentify } =
        storeToRefs(useSmarticoInit());

      if (
        isReadySmartico.value &&
        isSuccessCheckUserIdentify.value &&
        typeof window._smartico?.dp === 'function'
      ) {
        return Promise.resolve(true);
      }

      return new Promise<boolean>((resolve) => {
        const start = Date.now();
        const interval = setInterval(() => {
          if (
            isReadySmartico.value &&
            isSuccessCheckUserIdentify.value &&
            typeof window._smartico?.dp === 'function'
          ) {
            clearInterval(interval);
            resolve(true);

            return;
          }

          if (Date.now() - start >= timeoutMs) {
            clearInterval(interval);
            resolve(false);
          }
        }, 100);
      });
    }

    async function playMiniGame(miniGameId: number) {
      try {
        debugLog(`Attempting to play mini-game with ID: ${miniGameId}`);

        const ready = await waitForSmarticoReady();

        if (!ready) {
          debugLog(
            `⚠️ playMiniGame aborted: Smartico not ready after waiting (id=${miniGameId})`,
          );

          return;
        }

        window._smartico.dp(
          `dp:gf_saw&id=${miniGameId}&standalone=true&hide_main=true`,
        );
        setGamePlayed(true); // Set flag to true when a game is played
        triggerDelayedRefresh(); // Trigger delayed refresh after playing a game
      } catch (err: any) {
        debugLog(`⭕️ ~ dp:gf_saw ~ ${err.toString()}`);
      }
    }

    function registerCustomUpdateCallback(
      callback: (data: TMiniGameTemplate[]) => void,
    ) {
      if (typeof callback === 'function') {
        const exists = customUpdateCallbacks.value.some(
          (cb) => cb.toString() === callback.toString(),
        );

        if (!exists) {
          customUpdateCallbacks.value.push(callback);
          debugLog('✅ Custom mini-games update callback registered');
        }
      }
    }

    function unregisterCustomUpdateCallback(
      callback: (data: TMiniGameTemplate[]) => void,
    ) {
      const index = customUpdateCallbacks.value.findIndex(
        (cb) => cb.toString() === callback.toString(),
      );

      if (index !== -1) {
        customUpdateCallbacks.value.splice(index, 1);
        debugLog('✅ Custom mini-games update callback unregistered');
      }
    }

    return {
      miniGames,
      isLoading,
      gamePlayed,
      loadMiniGames,
      playMiniGame,
      setGamePlayed,
      triggerDelayedRefresh,
      registerCustomUpdateCallback,
      unregisterCustomUpdateCallback,
    };
  },

  storeConfig,
);
